import { useState } from 'react';

import { FlaskConical, Loader2 } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

import { BranchCommitPicker } from '@/components/BranchCommitPicker';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { useToast } from '@/hooks/use-toast';
import { useCreateReplay } from '@/hooks/useReplays';
import { extractErrorMessage } from '@/lib/utils';

import type { CallWithContact } from '@repo/shared-types';

type Modality = 'VOICE' | 'TEXT';

interface ReplayAsTestDialogProps {
  call: CallWithContact;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

const inferCallModality = (call: CallWithContact): Modality => {
  if (call.modality === 'TEXT') return 'TEXT';
  if (call.call_sid?.startsWith('text-')) return 'TEXT';
  if (['WHATSAPP', 'WEB_WIDGET', 'EMAIL'].includes(call.call_transport ?? '')) return 'TEXT';
  return 'VOICE';
};

/** Inner form that remounts when the dialog opens, resetting all form state. */
const ReplayForm = ({
  call,
  onOpenChange,
}: {
  call: CallWithContact;
  onOpenChange: (open: boolean) => void;
}) => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const createReplay = useCreateReplay();
  const [modality, setModality] = useState<Modality>(inferCallModality(call));
  const [commitId, setCommitId] = useState('');

  const handleRun = async () => {
    try {
      const replay = await createReplay.mutateAsync({
        source_call_id: call.id,
        modality,
        commit_id: commitId.trim() || undefined,
      });
      toast({ title: 'Replay started', description: 'Navigating to the replay detail page.' });
      onOpenChange(false);
      navigate(`/agent-improvement/replays/${replay.id}`);
    } catch (err) {
      toast({
        variant: 'destructive',
        title: 'Failed to start replay',
        description: extractErrorMessage(err),
      });
    }
  };

  return (
    <>
      <DialogHeader>
        <DialogTitle className="flex items-center gap-2">
          <FlaskConical className="h-5 w-5" />
          Replay as test
        </DialogTitle>
        <DialogDescription>
          Simulate this call using an AI customer agent and judge the result against the original.
        </DialogDescription>
      </DialogHeader>

      <div className="space-y-4 py-2">
        <div className="space-y-2">
          <p className="text-sm font-medium">Modality</p>
          <div className="flex gap-2">
            <Button
              type="button"
              variant={modality === 'VOICE' ? 'default' : 'outline'}
              size="sm"
              onClick={() => setModality('VOICE')}
            >
              Voice (Gemini Live)
            </Button>
            <Button
              type="button"
              variant={modality === 'TEXT' ? 'default' : 'outline'}
              size="sm"
              onClick={() => setModality('TEXT')}
            >
              Text
            </Button>
          </div>
        </div>

        <div className="space-y-2">
          <p className="text-sm font-medium">Pin to commit (optional)</p>
          <BranchCommitPicker
            configurationId={call.configuration_id ?? ''}
            value={commitId}
            onChange={setCommitId}
          />
        </div>
      </div>

      <DialogFooter>
        <Button variant="outline" onClick={() => onOpenChange(false)}>
          Cancel
        </Button>
        <Button onClick={() => void handleRun()} disabled={createReplay.isPending}>
          {createReplay.isPending ? (
            <>
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              Starting...
            </>
          ) : (
            'Run'
          )}
        </Button>
      </DialogFooter>
    </>
  );
};

export const ReplayAsTestDialog = ({ call, open, onOpenChange }: ReplayAsTestDialogProps) => (
  <Dialog open={open} onOpenChange={onOpenChange}>
    <DialogContent className="sm:max-w-md">
      {/* key forces remount on each open, resetting form state */}
      {open && <ReplayForm key={call.id} call={call} onOpenChange={onOpenChange} />}
    </DialogContent>
  </Dialog>
);
